import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import CompanySelectPage from "./pages/CompanySelectPage";
import LandingPage from "./pages/LandingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import PortalPage from "./pages/PortalPage";
import FeriePage from "./pages/FeriePage";
import CalendarPage from "./pages/CalendarPage";
import BustePagaPage from "./pages/BustePagaPage";
import TrasfertePage from "./pages/TrasfertePage";
import PresenzePage from "./pages/PresenzePage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ReportPage from "./pages/ReportPage";
import AdminPage from "./pages/AdminPage";
import EsgSustainabilityPage from "./pages/EsgSustainabilityPage";
import SicurezzaPage from "./pages/SicurezzaPage";
import SmartWorkingPage from "./pages/SmartWorkingPage";
import StraordinariPage from "./pages/StraordinariPage";
import CongediPage from "./pages/CongediPage";
import RubricaPage from "./pages/RubricaPage";
import OrganigrammaPage from "./pages/OrganigrammaPage";
import ComunicazioniPage from "./pages/ComunicazioniPage";
import DocumentiPage from "./pages/DocumentiPage";
import CertificatiPage from "./pages/CertificatiPage";
import FormazionePage from "./pages/FormazionePage";
import WelfarePage from "./pages/WelfarePage";
import ProfiloPage from "./pages/ProfiloPage";
import PolicyPage from "./pages/PolicyPage";
import CartaIntestataPage from "./pages/CartaIntestataPage";
import CambioPasswordPage from "./pages/CambioPasswordPage";
import GuidaPage from "./pages/GuidaPage";
import NovitaPage from "./pages/NovitaPage";
import AnteprimaPage from "./pages/AnteprimaPage";
import MaintenancePage from "./pages/MaintenancePage";
import CustomPage from "./pages/CustomPage";
import { enforceCanonicalLocation, setRobots } from "./seo";
import { getPrerender } from "./prerender";
import ShiftsPage from "./pages/ShiftsPage";
import SuperadminLogin from "./pages/superadmin/SuperadminLogin";
import SuperadminDashboard from "./pages/superadmin/SuperadminDashboard";
import ConsulenteLoginPage from "./pages/consulente/ConsulenteLoginPage";
import ConsulenteDashboard from "./pages/consulente/ConsulenteDashboard";
import SessionExpiryModal from "./components/SessionExpiryModal";
import RealtimeBridge from "./components/RealtimeBridge";
import ApprovalAlerts from "./components/ApprovalAlerts";
import PlanBanner from "./components/PlanBanner";
import DemoBanner from "./components/DemoBanner";
import MaintenanceAdminBanner from "./components/MaintenanceAdminBanner";
import MaintenanceNoticeBanner from "./components/MaintenanceNoticeBanner";

const ADMIN_ONLY = ["admin"];
const APPROVERS = ["responsabile", "admin"];

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#f0f2f5]">
    <div className="w-10 h-10 border-4 border-[#16345c] border-t-transparent rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ children, allow, internalOnly }) => {
  const { user, loading, maintenance } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (maintenance?.active && user.role !== "admin") return <MaintenancePage />;
  if (user.must_change_password) return <Navigate to="/cambio-password" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/" replace />;
  if (internalOnly && user.is_external) return <Navigate to="/" replace />;
  return children;
};

const RequireLogin = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

// Pagine pubbliche indicizzabili; tutte le altre (login, demo, area riservata) ricevono noindex.
const PUBLIC_PATH = /^\/($|p\/[^/]+\/?$|privacy-policy\/?$|cookie-policy\/?$)/;
const RobotsGuard = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (PUBLIC_PATH.test(pathname)) return undefined;
    setRobots("noindex, nofollow");
    return () => setRobots(null);
  }, [pathname]);
  return null;
};

// Home pubblica: chi NON è loggato vede la landing page; gli utenti autenticati vedono il portale.
const RootRoute = () => {
  const { user, loading, maintenance } = useAuth();
  // Home pre-renderizzata: mostra subito la landing mentre si verifica la sessione.
  if (loading) return getPrerender("/") ? <LandingPage /> : <Loader />;
  if (!user) return <LandingPage />;
  if (maintenance?.active && user.role !== "admin") return <MaintenancePage />;
  if (user.must_change_password) return <Navigate to="/cambio-password" replace />;
  return <PortalPage />;
};

function App() {
  useEffect(() => { enforceCanonicalLocation(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RobotsGuard />
          <Toaster position="top-right" richColors />
          <SessionExpiryModal />
          <RealtimeBridge />
          <ApprovalAlerts />
          <PlanBanner />
          <DemoBanner />
          <MaintenanceNoticeBanner />
          <MaintenanceAdminBanner />
          <Routes>
            <Route path="/anteprima" element={<AnteprimaPage />} />
            <Route path="/superadmin/login" element={<SuperadminLogin />} />
            <Route path="/superadmin/dashboard" element={<SuperadminDashboard />} />
            <Route path="/superadmin" element={<Navigate to="/superadmin/login" replace />} />
            <Route path="/consulente/login" element={<ConsulenteLoginPage />} />
            <Route path="/consulente" element={<ConsulenteDashboard />} />
            <Route path="/seleziona-societa" element={<PublicOnly><CompanySelectPage /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/cambio-password" element={<RequireLogin><CambioPasswordPage /></RequireLogin>} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/p/:slug" element={<CustomPage />} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/ferie" element={<ProtectedRoute internalOnly><FeriePage /></ProtectedRoute>} />
            <Route path="/trasferte" element={<ProtectedRoute internalOnly><TrasfertePage /></ProtectedRoute>} />
            <Route path="/presenze" element={<ProtectedRoute internalOnly><PresenzePage /></ProtectedRoute>} />
            <Route path="/calendario" element={<ProtectedRoute internalOnly><CalendarPage /></ProtectedRoute>} />
            <Route path="/turni" element={<ProtectedRoute internalOnly><ShiftsPage /></ProtectedRoute>} />
            <Route path="/buste-paga" element={<ProtectedRoute internalOnly><BustePagaPage /></ProtectedRoute>} />
            <Route path="/smart-working" element={<ProtectedRoute internalOnly><SmartWorkingPage /></ProtectedRoute>} />
            <Route path="/straordinari" element={<ProtectedRoute internalOnly><StraordinariPage /></ProtectedRoute>} />
            <Route path="/congedi" element={<ProtectedRoute internalOnly><CongediPage /></ProtectedRoute>} />
            <Route path="/rubrica" element={<ProtectedRoute><RubricaPage /></ProtectedRoute>} />
            <Route path="/organigramma" element={<ProtectedRoute><OrganigrammaPage /></ProtectedRoute>} />
            <Route path="/comunicazioni" element={<ProtectedRoute><ComunicazioniPage /></ProtectedRoute>} />
            <Route path="/documenti" element={<ProtectedRoute><DocumentiPage /></ProtectedRoute>} />
            <Route path="/certificati" element={<ProtectedRoute internalOnly><CertificatiPage /></ProtectedRoute>} />
            <Route path="/formazione" element={<ProtectedRoute internalOnly><FormazionePage /></ProtectedRoute>} />
            <Route path="/welfare" element={<ProtectedRoute><WelfarePage /></ProtectedRoute>} />
            <Route path="/guida" element={<ProtectedRoute><GuidaPage /></ProtectedRoute>} />
            <Route path="/novita" element={<ProtectedRoute><NovitaPage /></ProtectedRoute>} />
            <Route path="/profilo" element={<ProtectedRoute><ProfiloPage /></ProtectedRoute>} />
            <Route path="/policy" element={<ProtectedRoute allow={ADMIN_ONLY}><PolicyPage /></ProtectedRoute>} />
            <Route path="/carta-intestata" element={<ProtectedRoute allow={ADMIN_ONLY}><CartaIntestataPage /></ProtectedRoute>} />
            <Route path="/approvazioni" element={<ProtectedRoute allow={APPROVERS}><ApprovalsPage /></ProtectedRoute>} />
            <Route path="/report" element={<ProtectedRoute allow={ADMIN_ONLY}><ReportPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute allow={ADMIN_ONLY}><AdminPage /></ProtectedRoute>} />
            <Route path="/esg" element={<ProtectedRoute allow={ADMIN_ONLY}><EsgSustainabilityPage /></ProtectedRoute>} />
            <Route path="/sicurezza" element={<ProtectedRoute allow={ADMIN_ONLY}><SicurezzaPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
